import { flux } from '@/core/flux.module';
import type {
  ImpactReportCategory,
  ImpactReportColumnConfiguration,
  ImpactReportDashboardContent,
  ImpactReportUseCase,
  ImpactTotals,
  PredefinedDateRangeOptions,
  MapZoomSettings,
} from '@/impactReports/impactReport.types';
import { PUSH_WORKBENCH } from '@/core/flux.service';
import { addOrUpdateImpactReportConfiguration } from '@/impactReports/impactReport.utilities';
import { sqImpactReportStore } from '@/core/core.stores';
import type { ColumnDefinitionOutputV1 } from '@/sdk/model/ColumnDefinitionOutputV1';

export function setImpactReportTableId(tableId: string) {
  flux.dispatch('IMPACT_REPORT_SET_TABLE_ID', { tableId });
}

export function setImpactReportCategories(categories: ImpactReportCategory[] | undefined) {
  flux.dispatch('IMPACT_REPORT_SET_CATEGORIES', { categories });
}

export function setImpactReportStartDate(startDate: string | undefined) {
  flux.dispatch('IMPACT_REPORT_SET_START_DATE', { startDate }, PUSH_WORKBENCH);
}

export function setImpactReportEndDate(endDate: string | undefined) {
  flux.dispatch('IMPACT_REPORT_SET_END_DATE', { endDate }, PUSH_WORKBENCH);
}

export function setValueCaptureDateRange(startDate: string, endDate: string) {
  setImpactReportStartDate(startDate);
  setImpactReportEndDate(endDate);
}

export function setImpactReportLayoutDefinition(layoutDefinition: ImpactReportDashboardContent[]) {
  flux.dispatch('IMPACT_REPORT_SET_LAYOUT_DEFINITION', { layoutDefinition }, PUSH_WORKBENCH);
}

export function removeImpactReportContent(i: string) {
  flux.dispatch('IMPACT_REPORT_REMOVE_CONTENT', { i }, PUSH_WORKBENCH);
}

export function setForceTableReloadFlag(forceTableReload: boolean) {
  flux.dispatch('IMPACT_REPORT_SET_FORCE_TABLE_RELOAD', { forceTableReload });
}

export function setAggregationColumn(aggregationColumn: string | undefined) {
  flux.dispatch('IMPACT_REPORT_SET_AGGREGATION_COLUMN', { aggregationColumn });
}

export function setBiggestImpact(biggestImpact: number) {
  flux.dispatch('IMPACT_REPORT_SET_BIGGEST_IMPACT', { biggestImpact });
}

export function setUseCaseCount(useCaseCount: number) {
  flux.dispatch('IMPACT_REPORT_SET_USE_CASE_COUNT', { useCaseCount });
}

export function setOverallImpact(overallImpact: number) {
  flux.dispatch('IMPACT_REPORT_SET_OVERALL_IMPACT', { overallImpact });
}

export function setFilteredImpact(filteredImpact: number) {
  flux.dispatch('IMPACT_REPORT_SET_FILTERED_IMPACT', { filteredImpact });
}

export function setDisplayValueCaptureEditModal(displayEditModal: boolean) {
  flux.dispatch('IMPACT_REPORT_SET_DISPLAY_EDIT_MODAL', { displayEditModal });
}

export async function setImpactReportShowSite(showSite: boolean) {
  await addOrUpdateImpactReportConfiguration(sqImpactReportStore.tableId!, {
    ...sqImpactReportStore.impactReportConfiguration,
    showSite,
  });
  flux.dispatch('IMPACT_REPORT_SET_SHOW_SITE', { showSite });
}

export function setImpactReportShowSiteStoreOnly(showSite: boolean) {
  flux.dispatch('IMPACT_REPORT_SET_SHOW_SITE', { showSite });
}

export async function setImpactReportShowLocation(showLocation: boolean) {
  await addOrUpdateImpactReportConfiguration(sqImpactReportStore.tableId!, {
    ...sqImpactReportStore.impactReportConfiguration,
    showLocation,
  });
  flux.dispatch('IMPACT_REPORT_SET_SHOW_LOCATION', { showLocation });
}

export function setImpactReportShowLocationStoreOnly(showLocation: boolean) {
  flux.dispatch('IMPACT_REPORT_SET_SHOW_LOCATION', { showLocation });
}

export async function setImpactReportShowAssetPath(showAssetPath: boolean) {
  await addOrUpdateImpactReportConfiguration(sqImpactReportStore.tableId!, {
    ...sqImpactReportStore.impactReportConfiguration,
    showAssetPath,
  });
  flux.dispatch('IMPACT_REPORT_SET_SHOW_ASSET_PATH', { showAssetPath });
}

export function setImpactReportShowAssetPathStoreOnly(showAssetPath: boolean) {
  flux.dispatch('IMPACT_REPORT_SET_SHOW_ASSET_PATH', { showAssetPath });
}

export async function setImpactReportShowImpact(showImpact: boolean) {
  await addOrUpdateImpactReportConfiguration(sqImpactReportStore.tableId!, {
    ...sqImpactReportStore.impactReportConfiguration,
    showImpact,
  });
  flux.dispatch('IMPACT_REPORT_SET_SHOW_IMPACT', { showImpact });
}

export function setUpdateNominations(updateNominations: boolean) {
  flux.dispatch('IMPACT_REPORT_SET_UPDATE_NOMINATIONS', { updateNominations });
}

export async function setGlobalLayout(globalLayout: ImpactReportDashboardContent[]) {
  await addOrUpdateImpactReportConfiguration(sqImpactReportStore.tableId!, {
    ...sqImpactReportStore.impactReportConfiguration,
    globalLayout,
  });
  flux.dispatch('IMPACT_REPORT_SET_GLOBAL_LAYOUT', { globalLayout });
}

export function setGlobalLayoutStoreOnly(globalLayout: ImpactReportDashboardContent[]) {
  flux.dispatch('IMPACT_REPORT_SET_GLOBAL_LAYOUT', { globalLayout });
}

export function setDefinedTimeRange(definedTimeRange: PredefinedDateRangeOptions | undefined) {
  flux.dispatch('IMPACT_REPORT_SET_DEFINED_TIME_RANGE', { definedTimeRange }, PUSH_WORKBENCH);
}

export function setImpactReportTableData(tableData: ImpactReportDashboardContent[]) {
  flux.dispatch('IMPACT_REPORT_SET_TABLE_DATA', { tableData });
}

export function setImpactByLocation(tableData: ImpactReportUseCase[]) {
  flux.dispatch('IMPACT_REPORT_SET_IMPACT_BY_LOCATION', { tableData });
}

export function setCustomKPIColumns(kpiColumns: ColumnDefinitionOutputV1[]) {
  flux.dispatch('IMPACT_REPORT_SET_KPI_COLUMNS', { kpiColumns }, PUSH_WORKBENCH);
}

export function setAbsoluteTotals(absoluteTotals: ImpactTotals) {
  flux.dispatch('IMPACT_REPORT_SET_ABSOLUTE_TOTALS', { absoluteTotals });
}
export function setFilteredTotals(filteredTotals: ImpactTotals) {
  flux.dispatch('IMPACT_REPORT_SET_FILTERED_TOTALS', { filteredTotals });
}

export function setLargestKPIImpacts(largestKPIImpacts: ImpactTotals) {
  flux.dispatch('IMPACT_REPORT_SET_LARGEST_KPI_IMPACTS', { largestKPIImpacts });
}

export function setFilteredLocation(filteredLocation: string | undefined) {
  flux.dispatch('IMPACT_REPORT_SET_FILTERED_LOCATION', { filteredLocation });
}

export function setEditingTileId(editingTileId: string | undefined) {
  flux.dispatch('IMPACT_REPORT_SET_EDITING_TILE_ID', { editingTileId });
}

export function setColumnConfiguration(columnConfiguration: ImpactReportColumnConfiguration) {
  flux.dispatch('IMPACT_REPORT_SET_COLUMN_CONFIGURATION', { columnConfiguration }, PUSH_WORKBENCH);
}

export async function setMapZoom(mapZoom: MapZoomSettings | undefined) {
  await addOrUpdateImpactReportConfiguration(sqImpactReportStore.tableId!, {
    ...sqImpactReportStore.impactReportConfiguration,
    mapZoom,
  });
  flux.dispatch('IMPACT_REPORT_SET_MAP_ZOOM', { mapZoom }, PUSH_WORKBENCH);
}
